// Neo's receipt under its answer (TOM-354, cloud ADR 0302 §5, boards-v3.md
// "Chat board records"): each board the request changed, with Open board and
// one Undo for the whole request. Built from the turn's records, never from
// Neo's words. (The quick readings left the web in TOM-526.) A request that
// also changed the owner's own looks shares one Undo with their receipt
// (TOM-595): either card's undoes the whole request (useWholeTurnUndos).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { announceLooksChanged } from '../lib/boardLooks';
import { NEO_NAMES_UPDATED_EVENT } from '../lib/neoNames';
import { NEO_BOARD_UPDATED_EVENT, announceBoardItemRemoved } from '../lib/neoBoard';
import { looksTurnOutcome, type LooksReceiptPart } from '../lib/neoLooksReceipt';
import {
  receiptBoardHref,
  receiptCanUndo,
  receiptOutcomeText,
  receiptScope,
  receiptSupersededText,
  receiptTurnOutcome,
  undoReceiptPart,
  undoTurn,
  type BoardReceiptPart,
  type ReceiptLineKind,
  type ReceiptPart,
  type ReceiptUndoOutcome,
  type TurnUndoAnswer,
} from '../lib/neoReceipt';
import { receiptStepApplies, type ReceiptStep } from '../lib/receiptStep';
import { Icon } from './ui/Icon';

const LINE_ICON: Readonly<Record<ReceiptLineKind, string>> = {
  added: 'plus',
  changed: 'pencil',
  removed: 'minus',
  moved: 'move',
  resized: 'expand',
  plain: 'check',
};

/** False while the browser has no connection: Undo needs one (boards-v3.md "States"). */
function browserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Follows the browser's connection: a receipt's Undo needs one. */
export function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(browserOnline);
  useEffect(() => {
    const update = () => setOnline(browserOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * Tell the pages beside the chat what an Undo changed: an open board reads
 * itself again, a chart's Pin button whose item came off reads Pin, and the
 * boat's names are read again. `boardsChangedToo`: something else an open
 * board draws changed too, such as the owner's looks.
 */
function announceReceiptUndone(
  results: readonly (readonly [ReceiptPart, ReceiptUndoOutcome])[],
  boardsChangedToo = false
): void {
  let boardsChanged = boardsChangedToo;
  const namedBoats = new Set<string>();
  for (const [part, outcome] of results) {
    if (outcome.kind === 'undone' && part.kind === 'label') namedBoats.add(part.boatId);
    if (outcome.kind !== 'undone' || part.kind !== 'board') continue;
    boardsChanged = true;
    // A chart's Pin button that pinned an item the undo took off reads Pin
    // again. An undo of the last write only (before TOM-346) may leave them.
    if (outcome.removed || part.undoWhole) {
      for (const itemId of part.addedItemIds) announceBoardItemRemoved(itemId);
    }
  }
  if (boardsChanged) window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
  for (const boatId of namedBoats)
    window.dispatchEvent(new CustomEvent(NEO_NAMES_UPDATED_EVENT, { detail: { boatId } }));
}

/**
 * A request's one Undo, shared by its receipts (TOM-595): the whole-request
 * undo's progress and answer, and the Undo itself.
 */
export interface WholeTurnUndo {
  undoing: boolean;
  /** The turn's undo route's answer, once it has one; every part reads its outcome from it. */
  answer: TurnUndoAnswer | null;
  undo: () => void;
}

/**
 * The thread's whole-request Undos, by request (TOM-595). A request that
 * changed what the boat's owners share and the owner's own looks has two
 * receipts; either one's Undo undoes the whole request in one call to the
 * turn's undo route, and both draw the one answer. Returns the Undo of one
 * request, given both receipts' parts, which the answer is announced for.
 */
export function useWholeTurnUndos(): (
  turnRef: string,
  shared: readonly ReceiptPart[],
  looks: readonly LooksReceiptPart[]
) => WholeTurnUndo {
  const [states, setStates] = useState<
    Readonly<Record<string, { undoing: boolean; answer: TurnUndoAnswer | null }>>
  >({});
  // One key per request, kept across Try again, so a retry replays an undo
  // whose answer was lost instead of being refused as changed.
  const keys = useRef(new Map<string, string>());
  const running = useRef(new Set<string>());

  async function run(
    turnRef: string,
    shared: readonly ReceiptPart[],
    looks: readonly LooksReceiptPart[]
  ) {
    if (running.current.has(turnRef)) return;
    running.current.add(turnRef);
    setStates((prev) => ({
      ...prev,
      [turnRef]: { undoing: true, answer: prev[turnRef]?.answer ?? null },
    }));
    let key = keys.current.get(turnRef);
    if (key === undefined) {
      key = crypto.randomUUID();
      keys.current.set(turnRef, key);
    }
    const answer = await undoTurn(turnRef, key);
    const looksUndone = looks.filter((part) => looksTurnOutcome(part, answer).kind === 'undone');
    for (const part of looksUndone) announceLooksChanged(part.boatId);
    // An open board draws the owner's looks too: it reads itself again, once.
    announceReceiptUndone(
      shared.map((part) => [part, receiptTurnOutcome(part, answer)] as const),
      looksUndone.length > 0
    );
    running.current.delete(turnRef);
    setStates((prev) => ({ ...prev, [turnRef]: { undoing: false, answer } }));
  }

  return (turnRef, shared, looks) => ({
    undoing: states[turnRef]?.undoing ?? false,
    answer: states[turnRef]?.answer ?? null,
    undo: () => void run(turnRef, shared, looks),
  });
}

export function NeoReceipt({
  parts,
  boatName,
  onOpenBoard,
  superseded,
  panel,
  wholeTurn,
}: {
  parts: readonly ReceiptPart[];
  /** The boat's display name, for the scope lines. */
  boatName: (boatId: string) => string | null;
  /** Called as Open board navigates: the chat panel closes over the page. */
  onOpenBoard?: () => void;
  /** Keys of the parts a later request changed again: they offer no Undo (supersededParts). */
  superseded?: ReadonlySet<string>;
  /**
   * In the Edit with Neo panel (TOM-356) the board is beside the receipt, so it
   * offers no Open board: "Undo changes" and "Done", which closes the panel.
   */
  panel?: { onDone: () => void };
  /**
   * Set when the request also changed the owner's own looks (TOM-595): Undo
   * undoes the whole request, and every part reads its outcome from the answer.
   */
  wholeTurn?: WholeTurnUndo;
}) {
  const online = useBrowserOnline();
  const [ownOutcomes, setOutcomes] = useState<Readonly<Record<string, ReceiptUndoOutcome>>>({});
  const [ownUndoing, setUndoing] = useState(false);
  const turnAnswer = wholeTurn?.answer ?? null;
  const outcomes: Readonly<Record<string, ReceiptUndoOutcome>> =
    turnAnswer === null
      ? ownOutcomes
      : {
          ...ownOutcomes,
          ...Object.fromEntries(
            parts
              .filter((p) => p.undoVersion !== null)
              .map((p) => [p.key, receiptTurnOutcome(p, turnAnswer)])
          ),
        };
  const undoing = ownUndoing || wholeTurn?.undoing === true;
  // One key per part, kept across Try again, so a retry replays an undo whose
  // answer was lost instead of being refused as stale.
  const keys = useRef(new Map<string, string>());

  const isSuperseded = (part: ReceiptPart) =>
    superseded?.has(part.key) === true && outcomes[part.key] === undefined;

  // Each part's step, asked of its own route when the receipt is drawn
  // (TOM-381): a receipt drawn again after a reload, or in a reopened panel,
  // doesn't remember an Undo already done. Undo waits, disabled, for the answer.
  const [steps, setSteps] = useState<Readonly<Record<string, ReceiptStep>> | null>(null);
  const toAsk = parts.filter((p) => p.undoVersion !== null && superseded?.has(p.key) !== true);
  const askKey = toAsk.map((p) => `${p.key}@${p.undoVersion}`).join('|');
  const askRef = useRef(toAsk);
  askRef.current = toAsk;
  useEffect(() => {
    let live = true;
    void Promise.all(
      askRef.current.map(async (part) => [part.key, await receiptStepApplies(part)] as const)
    ).then((results) => {
      if (live) setSteps(Object.fromEntries(results));
    });
    return () => {
      live = false;
    };
  }, [askKey]);
  const checking = steps === null && toAsk.length > 0;
  const stepOf = (part: ReceiptPart) =>
    outcomes[part.key] === undefined ? steps?.[part.key] : undefined;
  const isGone = (part: ReceiptPart) => stepOf(part) === 'gone' || stepOf(part) === 'missing';

  const undoable = parts.filter((p) => p.undoVersion !== null && !isSuperseded(p) && !isGone(p));
  const pending = undoable.filter((p) => {
    const outcome = outcomes[p.key];
    return outcome === undefined || outcome.kind === 'failed';
  });
  const failed = undoable.some((p) => outcomes[p.key]?.kind === 'failed');

  async function undo() {
    if (undoing || pending.length === 0) return;
    if (wholeTurn !== undefined) {
      wholeTurn.undo();
      return;
    }
    setUndoing(true);
    const results = await Promise.all(
      pending.map(async (part) => {
        let key = keys.current.get(part.key);
        if (key === undefined) {
          key = crypto.randomUUID();
          keys.current.set(part.key, key);
        }
        return [part, await undoReceiptPart(part, key)] as const;
      })
    );
    announceReceiptUndone(results);
    setOutcomes((prev) => ({
      ...prev,
      ...Object.fromEntries(results.map(([p, o]) => [p.key, o])),
    }));
    setUndoing(false);
  }

  const removed = (key: string) => {
    const outcome = outcomes[key];
    return outcome?.kind === 'undone' && outcome.removed;
  };
  const openable = parts.filter(
    (p): p is BoardReceiptPart =>
      p.kind === 'board' && p.exists && !removed(p.key) && stepOf(p) !== 'missing'
  );
  const showUndo = receiptCanUndo(parts) && pending.length > 0;
  // In the panel, "Undo changes" stays, disabled, once the change can no longer
  // be undone (boards-v3.md); it goes once the undo is done.
  const allUndone =
    undoable.length > 0 && undoable.every((p) => outcomes[p.key]?.kind === 'undone');
  const panelUndo = panel !== undefined && receiptCanUndo(parts) && !allUndone;

  return (
    <section className="neo-receipt" aria-label="What Neo changed" data-testid="neo-receipt">
      {parts.map((part, i) => {
        const outcome = outcomes[part.key];
        return (
          <div
            key={part.key}
            className={`neo-receipt__part${i > 0 ? ' neo-receipt__part--split' : ''}`}
          >
            <div className="neo-receipt__head">
              <span className="neo-receipt__tile" aria-hidden="true">
                <Icon
                  name={
                    part.kind === 'board'
                      ? 'boards'
                      : part.kind === 'equipment'
                        ? 'engine'
                        : part.kind === 'notebook'
                          ? 'notebook'
                          : 'user'
                  }
                  size={16}
                />
              </span>
              <span className="neo-receipt__titles">
                <b className="neo-receipt__heading">{part.heading}</b>
                <small className="neo-receipt__scope">
                  {receiptScope(part, boatName(part.boatId))}
                </small>
              </span>
            </div>
            {part.lines.length > 0 ? (
              <ul className="neo-receipt__lines">
                {part.lines.map((line) => (
                  <li
                    key={line.key}
                    className={`neo-receipt__line neo-receipt__line--${line.kind}`}
                  >
                    <Icon name={LINE_ICON[line.kind]} size={16} />
                    <span className="sr-only">{line.spoken}</span>
                    <span aria-hidden="true">{line.text}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {outcome !== undefined && outcome.kind !== 'failed' ? (
              <p className="neo-receipt__note" role="status">
                {receiptOutcomeText(part, outcome)}
              </p>
            ) : part.undoVersion !== null && isSuperseded(part) ? (
              <p className="neo-receipt__note">{receiptSupersededText(part)}</p>
            ) : isGone(part) ? (
              <p className="neo-receipt__note">
                {receiptOutcomeText(part, {
                  kind: 'refused',
                  reason: stepOf(part) === 'missing' ? 'gone' : 'nothing',
                })}
              </p>
            ) : null}
          </div>
        );
      })}
      {failed && !undoing ? (
        <p className="neo-receipt__note" role="alert">
          Couldn’t undo. Nothing changed. Check the connection and try again.
        </p>
      ) : null}
      {showUndo && !online ? (
        <p className="neo-receipt__note">You’re offline. This needs a connection.</p>
      ) : null}
      {panel !== undefined ? (
        <div className="neo-receipt__actions">
          {panelUndo ? (
            <button
              type="button"
              className="tb-btn tb-btn--gray tb-btn--sm neo-receipt__button"
              onClick={() => void undo()}
              disabled={undoing || checking || !online || pending.length === 0}
            >
              {failed ? null : <Icon name="undo" size={15} />}
              {undoing ? 'Undoing…' : failed ? 'Try again' : 'Undo changes'}
            </button>
          ) : null}
          <button
            type="button"
            className="tb-btn tb-btn--tinted tb-btn--sm neo-receipt__button"
            onClick={panel.onDone}
          >
            Done
          </button>
        </div>
      ) : openable.length > 0 || showUndo ? (
        <div className="neo-receipt__actions">
          {openable.map((part) => (
            <Link
              key={part.key}
              className="tb-btn tb-btn--tinted tb-btn--sm neo-receipt__button"
              to={receiptBoardHref(part)}
              onClick={onOpenBoard}
            >
              {openable.length === 1 ? 'Open board' : `Open ${part.boardTitle}`}
            </Link>
          ))}
          {showUndo ? (
            <button
              type="button"
              className="tb-btn tb-btn--gray tb-btn--sm neo-receipt__button"
              onClick={() => void undo()}
              disabled={undoing || checking || !online}
            >
              {failed ? null : <Icon name="undo" size={15} />}
              {undoing ? 'Undoing…' : failed ? 'Try again' : 'Undo'}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
